import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  makeStyles,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SocItem } from "../../../../domain/SocItem";
import { Controller, useForm } from "react-hook-form";
import { Model } from "@ofqual/portal-core/core/types";
import { Autocomplete } from "@material-ui/lab";
import { TextField } from "@ofqual/portal-components/components/TextField";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { Error } from "../../../common/Error";
import { usersService } from "../../../../services";
import { authSelector, feedSelector } from "../../../../redux/selectors";
import {
  Activity,
  ActivityFeedItem,
  ActivityFeedItemDefinition,
  ActivitySegmentHeader,
} from "@ofqual/portal-activity-feed";
import { ActivityType } from "../../../../domain/ActivityType";
import { segmentList } from "@ofqual/portal-core/core/utils/listHelpers";
import { formatAsTodayYesterdayOrDate } from "@ofqual/portal-core/core/utils/dateUtils";
import { buildDefinitions } from "../buildDefinitions";
import { sendInternalMessage } from "../../../../redux/actions";

const useStyles = makeStyles(() => ({
  actions: {
    padding: 15,
  },
}));

export const InternalMessageDialog: React.FC<{
  onClose: () => void;
  resource: SocItem;
}> = ({ onClose, resource }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { token } = useSelector(authSelector);

  const [upns, setUpns] = useState([] as Model[]);

  useEffect(() => {
    usersService.getUsers({ token }).then((response) => setUpns(response));
  }, [token]);

  const { handleSubmit, control, errors } = useForm({
    defaultValues: {
      subject: null,
      message: null,
      files: [],
      recipients: null,
    },
  });

  const handleSendMessage = (data: {
    files: File[];
    subject: string;
    message: string;
    recipients: Model[];
  }) => {
    dispatch(
      sendInternalMessage(
        resource.id,
        data.files,
        data.subject,
        data.message,
        data.recipients.map((x) => x.name)
      )
    );
    onClose();
  };

  const Values = (): Activity[] => {
    const activity = useSelector(feedSelector);
    const value = activity[resource.id];
    const items = value.response.data
      .filter((item) => item.activityType === ActivityType.SocInternalMessage)
      .sort((a, b) => {
        let dateA = new Date(a.createdDate);
        let dateB = new Date(b.createdDate);
        return dateB.getTime() - dateA.getTime();
      });

    return items;
  };

  const segmentedActivities = segmentList(
    Values(),
    (item) => formatAsTodayYesterdayOrDate(item.createdDate) || ""
  );

  const definitions: ActivityFeedItemDefinition<any>[] = buildDefinitions(
    () => {},
    resource
  );

  return (
    <Dialog open={true} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle data-test="activity-title">
        Send an internal message
      </DialogTitle>
      <Divider />
      <DialogContent style={{ minHeight: "350px" }}>
        <form>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Controller
                name="recipients"
                control={control}
                rules={{
                  required: true,
                }}
                render={({ onChange, value, ...props }) => (
                  <Autocomplete
                    multiple
                    autoSelect
                    options={upns}
                    ChipProps={{ color: "primary" }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        data-test="recipient-field"
                        name="recipients"
                        variant="outlined"
                        label="Recipients"
                      />
                    )}
                    getOptionLabel={(option) => option.name}
                    getOptionSelected={(option, value) => option.id === value}
                    onChange={(e: any, data: any) => onChange(data)}
                    {...props}
                  />
                )}
              />
              {errors.recipients && (
                <Error data-test="internal-message-recipient-field-error">
                  This field is required
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="subject"
                rules={{ required: true, maxLength: 250 }}
                render={({ onChange, value }) => (
                  <TextField
                    onChange={onChange}
                    value={value}
                    data-test="internal-message-subject-field"
                    label="Subject"
                    required
                  />
                )}
                required
              />
              {errors.subject?.type === "required" && (
                <Error data-test="internal-message-subject-validation">
                  This field is required
                </Error>
              )}
              {errors.subject?.type === "maxLength" && (
                <Error data-test="internal-message-subject-validation">
                  Must be less than 250 characters
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="message"
                rules={{ required: true, maxLength: 5000 }}
                render={({ onChange, value }) => (
                  <MarkdownEditor
                    onChange={onChange}
                    value={value}
                    dataTest="create-note-text-field"
                  />
                )}
              />
              {errors.message?.type === "required" && (
                <Error data-test="internal-message-validation">
                  This field is required
                </Error>
              )}
              {errors.message?.type === "maxLength" && (
                <Error data-test="internal-message-validation">
                  Must be less than 250 characters
                </Error>
              )}
            </Grid>
            <Grid item xs={12}>
              <Controller
                control={control}
                name="files"
                as={
                  <FileInput
                    multiple={true}
                    dataTest="create-note-add-attachment-button"
                  />
                }
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Button
          onClick={onClose}
          color="primary"
          data-test="close-add-internal-dialog"
        >
          Close
        </Button>
        <Button
          onClick={handleSubmit(handleSendMessage)}
          color="primary"
          data-test="add-internal-message-button"
        >
          Send
        </Button>
      </DialogActions>
      <Divider />
      <DialogContent>
        {segmentedActivities.map((segment) => (
          <div key={segment.label}>
            <ActivitySegmentHeader label={segment.label} />
            {segment.data.map((item) => (
              <ActivityFeedItem
                key={item.id}
                activity={item}
                definition={definitions.find(
                  (def) => def.activityType === item.activityType
                )}
              />
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
